/** Oracle inside the palette — conversational intelligence channel.
 * Data-first: the structured card renders instantly; the analyst narrative
 * streams in behind it via the deferred narration endpoint. */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "./commandApi";
import { useCallsign, refreshCallsign } from "./useCallsign";
import IntelCard from "./IntelCard";

const MODES = [
  ["analyst", "ANALYST"],
  ["archivist", "ARCHIVIST"],
  ["case_officer", "CASE OFFICER"],
  ["strategist", "STRATEGIST"],
];

const ModeSelector = () => {
  const identity = useCallsign();
  const current = identity?.oracle_mode || "analyst";
  const setMode = (m) => {
    if (m === current) return;
    api("/command/identity/mode", { method: "put", data: { mode: m } })
      .then(() => refreshCallsign())
      .catch(() => {});
  };
  return (
    <div className="flex items-center gap-1 flex-wrap mb-2" data-testid="oracle-mode-selector">
      <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#5A554C] mr-1">Mode</span>
      {MODES.map(([id, label]) => (
        <button key={id} type="button" onClick={() => setMode(id)}
                data-testid={`oracle-mode-${id}`}
                className={`font-mono text-[8px] uppercase tracking-[0.15em] px-2 py-0.5 border transition-colors ${
                  current === id
                    ? "border-[#D92D20] text-[#E4DFD3] bg-[#1a0808]"
                    : "border-[#2D2A26] text-[#5A554C] hover:text-[#A39E93] hover:border-[#3D3A36]"}`}>
          {label}
        </button>
      ))}
    </div>
  );
};

const ChannelFrame = ({ open }) => (
  <div className="my-2 channel-frame" data-testid={open ? "channel-open" : "channel-close"}>
    <div className="border-t border-[#D92D20]/25" />
    <div className="py-1 text-center font-mono text-[8px] uppercase tracking-[0.4em] text-[#7B7168]">
      {open
        ? <>Secure Channel Established · Encryption Verified · <span className="text-[#3FB950]">Oracle Online</span></>
        : <>Analysis Complete · Secure Channel Closed</>}
    </div>
    <div className="border-t border-[#D92D20]/25" />
  </div>
);

const OracleInline = forwardRef(({ onNavigate, getContext, callsign }, ref) => {
  const [turns, setTurns] = useState([]);
  const historyRef = useRef([]);
  const endRef = useRef(null);

  const ask = async (query) => {
    const q = (query || "").trim();
    if (!q) return;
    const idx = turns.length;
    setTurns((t) => [...t, { q, pending: true }]);
    try {
      const resp = await api("/command/oracle/ask", {
        method: "post",
        data: { query: q, history: historyRef.current.slice(-4), context: getContext?.() || {} },
      });
      setTurns((t) => t.map((turn, i) => (i === idx
        ? { q, resp, narrative: resp.narrative, pending: false,
            narrating: !!resp.narration_id }
        : turn)));
      if (resp.narration_id) {
        api(`/command/oracle/narration/${resp.narration_id}`)
          .then((n) => {
            historyRef.current.push({ q, a: n.narrative || "", role: resp.header?.role });
            setTurns((t) => t.map((turn, i) => (i === idx
              ? { ...turn, narrative: n.narrative, narrating: false } : turn)));
          })
          .catch(() => setTurns((t) => t.map((turn, i) => (i === idx
            ? { ...turn, narrating: false } : turn))));
      } else if (resp.narrative) {
        historyRef.current.push({ q, a: resp.narrative, role: resp.header?.role });
      }
    } catch {
      setTurns((t) => t.map((turn, i) => (i === idx
        ? { q, pending: false, error: true,
            narrative: "Secure channel interrupted. Try again." } : turn)));
    }
  };

  useImperativeHandle(ref, () => ({ ask }));
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [turns]);

  return (
    <div className="px-4 py-2" data-testid="oracle-inline">
      <ModeSelector />
      {turns.length === 0 && (
        <div className="py-4 font-mono text-[10px] text-[#5A554C]" data-testid="oracle-inline-empty">
          <span className="text-[#D92D20]">ORACLE</span> — secure channel open{callsign ? <>, <span className="text-[#E0A526]">{callsign}</span></> : ""}.
          Ask anything: "What changed today?" · "Review this investigation" ·
          "What should I investigate next?"
        </div>
      )}
      {turns.map((t, i) => (
        <div key={i} className="mb-3" data-testid={`oracle-turn-${i}`}>
          <div className="font-mono text-[11px] text-[#7B7168]">
            <span className="text-[#D92D20]">&gt;</span> {t.q}
          </div>
          {t.pending && (
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5A554C] mt-1 animate-pulse"
                 data-testid="oracle-pending">
              Cross-referencing…
            </div>
          )}
          {t.resp?.authorized === false && (
            <div className="font-mono text-[11px] text-[#E0A526] mt-1" data-testid="oracle-denial">
              {t.narrative}
            </div>
          )}
          {t.resp?.authorized !== false && t.resp?.operational && t.resp?.card && (
            <ChannelFrame open />
          )}
          {t.resp?.authorized !== false && t.resp?.card && (
            <IntelCard card={t.resp.card} onNavigate={onNavigate}
                       onAction={() => ask("Generate founder briefing")} />
          )}
          {t.resp?.authorized !== false && !t.pending && (
            t.narrative ? (
              <>
              <div className="mt-2 flex gap-2 items-start" data-testid="oracle-narrative">
                <Sparkles size={11} className="text-[#D92D20] mt-0.5 shrink-0" />
                <p className="font-mono text-[11px] leading-relaxed text-[#C9C2B4]">{t.narrative}</p>
              </div>
              {t.resp?.operational && <ChannelFrame open={false} />}
              </>
            ) : t.narrating ? (
              <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#5A554C] animate-pulse"
                   data-testid="oracle-narrating">
                Oracle composing assessment…
              </div>
            ) : null
          )}
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
});

export default OracleInline;
